import {
  camelCase,
  capitalize as capitalizeLodash,
  escape,
  escapeRegExp,
  isNil,
  sampleSize,
  split
} from 'lodash';
import { RegexPattern } from '@/modules/core/app/constants/RegexPattern';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

/**
 * quickBrown to quick/brown
 */
export function turnCamelCaseToSlashCase(str: string): string {
  const separator: string = '/';
  return str
    .replace(/([a-z\d])([A-Z])/g, `$1${separator}$2`)
    .replace(/([A-Z]+)([A-Z][a-z\d]+)/g, `$1${separator}$2`)
    .toLowerCase();
}

/**
 * quick_brown to QuickBrown
 */
export function fromSnakeCaseToPascalCase(str: string): string {
  return capitalize(camelCase(str), true);
}

/**
 * quick_brown to Quick Brown
 */
export function fromSnakeCaseToPascalCaseWithSpaces(str: string): string {
  return capitalizeAll(_underscoreToSpace(str));
}

/**
 * quick_brown to Quick brown
 */
export function fromSnakeCaseToSpacesWithCapitalLetter(str: string): string {
  return capitalize(_underscoreToSpace(str), true);
}

/**
 * quick_brown converted to quick-brown
 */
export function fromSnakeCaseToKebabCase(str: string): string {
  return _underscoreToHyphen(str);
}

/**
 * QuickBrown to quick-brown
 */
export function fromPascalCaseToKebabCase(str: string): string {
  return str.replace(/([a-z0–9])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * 1989-12-09 to 1989/12/09
 */
export function fromHyphenISOtoForwardSlashISO(isoDate: string): string {
  return capitalizeAll(_hyphenToForwardSlash(isoDate));
}

/**
 * @todo: onlyFirstLetter does nothing
 */
export function capitalize(str: string, onlyFirstLetter: boolean = false): string {
  return onlyFirstLetter ? str.charAt(0).toUpperCase() + str.slice(1) : capitalizeLodash(str);
}

export function isString(value: unknown): boolean {
  return typeof value === typeof '';
}

export function capitalizeAll(str: string): string {
  return str.replace(
    /\w\S*/g,
    (substr: string) => substr.charAt(0).toUpperCase() + substr.substr(1).toLowerCase()
  );
}

export function boolToString(value: boolean | undefined | null): string {
  if (isNil(value)) {
    return 'N/A';
  }

  return value ? 'yes' : 'no';
}

export function stringToBool(str: string): boolean {
  return ['yes', '1', 'true'].includes(str?.toLowerCase());
}

export function isValidEmail(value: string): boolean {
  return RegexPattern.EMAIL.test(value);
}

export function isValidURL(value: string): boolean {
  return RegexPattern.URL.test(value.trim());
}

export function compareFunction(
  firstString: string = '',
  secondString: string = '',
  isAscending: boolean
): number {
  const result: number = firstString.toLowerCase() > secondString.toLowerCase() ? 1 : -1;

  return isAscending ? result : -1 * result;
}

export function extractFirstPart(str: string, divider: string = '/'): string | undefined {
  return str.split(divider).shift();
}

export function extractLastPart(str: string, divider: string = '/'): string | undefined {
  return str.split(divider).pop();
}

export function removeLastPart(str: string, divider: string = '/'): string {
  return str.substring(0, str.lastIndexOf(divider));
}

/**
 * 1 => first
 * 2 => second
 * .. and so on
 */
export function stringifyNumber(num: number, includeZero: boolean = false): string {
  const special: string[] = [
    'first',
    'second',
    'third',
    'fourth',
    'fifth',
    'sixth',
    'seventh',
    'eighth',
    'ninth',
    'tenth',
    'eleventh',
    'twelfth',
    'thirteenth',
    'fourteenth',
    'fifteenth',
    'sixteenth',
    'seventeenth',
    'eighteenth',
    'nineteenth'
  ];

  if (includeZero) {
    special.unshift('zeroth');
  }

  const deca: string[] = ['twent', 'thirt', 'fort', 'fift', 'sixt', 'sevent', 'eight', 'ninet'];

  if (num < 20) {
    return special[num];
  }

  if (num % 10 === 0) {
    return `${deca[Math.floor(num / 10) - 2]}ieth`;
  }

  return `${deca[Math.floor(num / 10) - 2]}y-${special[num % 10]}`;
}

/**
 * Filter undefined or null values to construct name based on given arguments
 * ['quick', null, undefined, 'brown'] becomes 'quick brown'
 */
export function reduceJoin(strings: (string | undefined | null)[] = [], separator = ' '): string {
  const notNullArgs: string[] = strings.filter(Boolean) as string[];
  return notNullArgs.reduce((accum: string, arg: string, index: number) => {
    accum += arg;

    if (index !== notNullArgs.length - 1) {
      accum += separator;
    }

    return accum;
  }, '');
}

/**
 * https://stackoverflow.com/a/37511463/16556511
 */
export function removeAccents(str: string): string {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function parseIdFromParam(param: string): number | undefined {
  const paramParts: string[] = param.split('|');

  if (paramParts.length < 2) {
    return undefined;
  }

  return parseInt(paramParts[1]) ?? undefined;
}

export function randomString(length: number): string {
  return sampleSize('abcdefghijklmnopqrstuvwxyz', length).join('');
}

export function getTextFromHTML(value?: string): string {
  const parser: DOMParser = new DOMParser();
  const document: Document = parser.parseFromString(value ?? '', 'text/html');

  return document.body.textContent || '';
}

function _underscoreToSpace(str: string): string {
  return str.replace(/_/g, ' ');
}

function _hyphenToForwardSlash(str: string): string {
  return str.replace(/-/g, '/');
}

function _underscoreToHyphen(str: string): string {
  return str.replace(/_/g, '-');
}

export function cleanHTMLTags(text: string, removeTags: string[] = []): string {
  return DOMPurify.sanitize(text, { FORBID_TAGS: removeTags, ADD_ATTR: ['target', 'rel'] });
}

export function maskLastDigits(str: string, digits: number = 4): string {
  return str?.length > digits ? '*'.repeat(digits) + str.substring(str.length - digits) : '';
}

export function removeDoubleQuotations(str: string): string {
  return str.replace(/"+/g, '');
}

export function markdownToHtml(markdown: string): string {
  return marked(markdown, { gfm: true });
}

export function highlightSearchTerm(text: string, searchTerm: string): string {
  const escapedText: string = escape(text);
  const trimmedTerm: string = searchTerm.trim();

  if (!trimmedTerm) {
    return escapedText;
  }

  const pattern: RegExp = new RegExp(escapeRegExp(escape(trimmedTerm)), 'gi');

  return escapedText.replace(pattern, (match: string) => `<mark>${match}</mark>`);
}

/**
 * Split a text string into paragraphs by double newlines (\n\n).
 * Strips the Unicode Line Separator (U+2028) before splitting.
 * Returns an empty array for falsy input.
 */
export function splitTextParagraphs(text?: string | null): string[] {
  if (!text) {
    return [];
  }
  return split(text.replace(/\u2028/g, ''), '\n\n');
}
